/** User-visible summary for non-job Think tool parts in the dock. */

/** Must match agent `WAKE_NEEDS_HUMAN_WRITE_TOOLS` (job-wake.ts). */
export const WAKE_NEEDS_HUMAN_WRITE_TOOLS = [
  "commit_project",
  "import_project",
  "circuit_diff",
  "agent_findings",
  "sync_project",
  "create_issue",
  "upload_project_material",
  "cancel_job",
] as const;

export const WAKE_NEEDS_HUMAN_ERROR = "needs_human" as const;

/** Must match agent `WAKE_NEEDS_HUMAN_TOOL_CALL_ID` (job-wake.ts). */
export const WAKE_NEEDS_HUMAN_TOOL_CALL_ID = /^[A-Za-z0-9_-]{1,64}$/;

export type ThinkToolSurface =
  | { kind: "error"; message: string; toolName: string }
  | { kind: "result-error"; message: string; toolName: string }
  | { kind: "result-ok"; message: string; toolName: string }
  | {
      kind: "needs-human";
      message: string;
      toolName: string;
      toolCallId: string;
    };

function errorMessageFromOutput(output: unknown): string | null {
  if (output == null || typeof output !== "object") return null;
  const rec = output as Record<string, unknown>;
  if (typeof rec.message === "string" && rec.message.trim()) return rec.message.trim();
  if (typeof rec.error === "string" && rec.error.trim()) return rec.error.trim();
  return null;
}

function okHintFromOutput(output: unknown, toolName: string): string | null {
  if (output == null || typeof output !== "object") return null;
  const rec = output as Record<string, unknown>;
  if (rec.ok === false) return null;
  if (typeof rec.git_oid === "string" && rec.git_oid) {
    return `${toolName} succeeded (revision published).`;
  }
  if (typeof rec.revision_id === "string" && rec.revision_id) {
    return `${toolName} succeeded (${rec.revision_id}).`;
  }
  if (rec.ok === true) return `${toolName} completed.`;
  return null;
}

export function isWakeNeedsHumanWriteTool(name: string): boolean {
  return (WAKE_NEEDS_HUMAN_WRITE_TOOLS as readonly string[]).includes(name);
}

export function parseWakeNeedsHuman(
  output: unknown,
): { toolName: string; message: string; toolCallId: string } | null {
  if (output == null || typeof output !== "object") return null;
  const rec = output as Record<string, unknown>;
  if (rec.error !== WAKE_NEEDS_HUMAN_ERROR) return null;
  const toolName = typeof rec.tool === "string" ? rec.tool.trim() : "";
  if (!toolName || !isWakeNeedsHumanWriteTool(toolName)) return null;
  const toolCallId = typeof rec.toolCallId === "string" ? rec.toolCallId.trim() : "";
  if (!toolCallId || !WAKE_NEEDS_HUMAN_TOOL_CALL_ID.test(toolCallId)) return null;
  const message =
    typeof rec.message === "string" && rec.message.trim()
      ? rec.message.trim()
      : "Reconnect to approve this action.";
  return { toolName, message, toolCallId };
}

export function surfaceNonJobThinkToolPart(part: {
  type: string;
  state?: string;
  output?: unknown;
  errorText?: string;
  toolName?: string;
}): ThinkToolSurface | null {
  const toolName =
    part.type === "dynamic-tool"
      ? (part.toolName ?? "tool")
      : part.type.replace(/^tool-/, "");
  const state = part.state ?? "";
  if (state === "output-error") {
    const message = part.errorText?.trim() || `${toolName} failed.`;
    return { kind: "error", message, toolName };
  }
  if (state === "output-available") {
    const needsHuman = parseWakeNeedsHuman(part.output);
    if (needsHuman) {
      return {
        kind: "needs-human",
        message: needsHuman.message,
        toolName: needsHuman.toolName,
        toolCallId: needsHuman.toolCallId,
      };
    }
    const err = errorMessageFromOutput(part.output);
    if (err) return { kind: "result-error", message: err, toolName };
    const ok = okHintFromOutput(part.output, toolName);
    if (ok) return { kind: "result-ok", message: ok, toolName };
  }
  return null;
}
